import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './tabContent.module.scss';
import { ActionButton } from 'components/actionButton';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { transferBalance } from 'store/authSlice';
import { Alert } from 'components/alert/alert';
import { SwapIcon } from 'components/icons';
import { generateIdempotencyKey } from 'utils/idempotency';

interface WithdrawalTabProps {
  onSwap?: () => void;
}

const WithdrawalTab: React.FC<WithdrawalTabProps> = ({ onSwap }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  // Use fallback to 0.00 if balance is missing
  const currentBalance = user?.balances?.[0];
  const walletBalance = currentBalance?.withdrawable || '0.00';
  const vaultBalance = currentBalance?.vault || '0.00';
  const vaultAvailableBalance = currentBalance?.vaultAvailable || '0.00';

  const currencyCode = currentBalance?.currency || 'USD';

  const [amount, setAmount] = useState('');
  const [alert, setAlert] = useState<{
    message: string;
    type: 'success' | 'error';
    variant: 'local' | 'toast';
  } | null>(null);
  const [isTransferring, setIsTransferring] = useState(false);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\$/g, '');
    if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
      setAmount(rawValue);
      setAlert(null);
    }
  };

  const handleWithdrawal = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setAlert({
        message: t('vault.messages.invalidAmount', 'Please enter a valid amount'),
        type: 'error',
        variant: 'local',
      });
      return;
    }

    if (parseFloat(amount) > parseFloat(vaultBalance)) {
      setAlert({
        message: t('vault.messages.exceedsVault', 'Amount exceeds vault balance'),
        type: 'error',
        variant: 'local',
      });
      return;
    }

    setAlert(null);
    setIsTransferring(true);

    try {
      const resultAction = await dispatch(
        transferBalance({
          fromType: 'vault',
          toType: 'main',
          amount,
          currencyCode,
          idempotencyKey: generateIdempotencyKey(),
          // lockedUntil is omitted for vault -> main
        }),
      );

      if (transferBalance.fulfilled.match(resultAction)) {
        setAlert({
          message: t('vault.messages.transferredFromVault', 'Balance transferred from vault.'),
          type: 'success',
          variant: 'toast',
        });
        setAmount('');
      } else {
        setAlert({
          message:
            (resultAction.payload as string) ||
            t('vault.messages.transferFailed', 'Transfer failed'),
          type: 'error',
          variant: 'toast',
        });
      }
    } catch (err) {
      setAlert({
        message: t('vault.messages.unexpectedError', 'An unexpected error occurred'),
        type: 'error',
        variant: 'local',
      });
    } finally {
      setIsTransferring(false);
    }
  };

  return (
    <div className={styles.tabWrapper}>
      <Alert
        message={alert?.message}
        type={alert?.type}
        variant={alert?.variant}
        className={styles.alert}
        onClose={() => setAlert(null)}
      />

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <span className={styles.cardTitle}>{t('vault.balance', 'Balance')}</span>
          <span className={styles.cardSubtitle}>
            {t('vault.availableBalance', 'Available Balance: ${{amount}}', {
              amount: walletBalance,
            })}
          </span>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <span className={styles.cardTitle}>{t('vault.vault', 'Vault')}</span>
          <div className={styles.cardSubtitleColumn}>
            <div className={styles.cardSubtitleRow}>
              <span className={styles.cardSubtitle}>
                {t('vault.vaultBalance', 'Balance: ${{amount}}', { amount: vaultBalance })}
              </span>
              <span className={styles.cardSubtitle}>/</span>
              <span className={styles.cardSubtitle}>
                {t('vault.vaultAvailable', 'Available: ${{amount}}', {
                  amount: vaultAvailableBalance,
                })}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.inputRow}>
          <input
            type="text"
            className={styles.amountInput}
            placeholder="$0.00"
            value={amount ? `$${amount}` : ''}
            onChange={handleAmountChange}
          />
          <button
            className={styles.maxBtn}
            onClick={() => {
              setAmount(vaultAvailableBalance);
              setAlert(null);
            }}
          >
            {t('vault.max', 'Max')}
          </button>
        </div>
      </div>

      <div className={styles.swapWrapper}>
        <button className={styles.swapBtn} onClick={onSwap}>
          <SwapIcon width="16" height="16" />
        </button>
      </div>

      <ActionButton onClick={handleWithdrawal} isLoading={isTransferring}>
        {t('vault.withdrawFromVault', 'Withdraw from Vault')}
      </ActionButton>
    </div>
  );
};

export default WithdrawalTab;
